import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Shield, Sparkles, Crown } from 'lucide-react';

/**
 * Tier-specific badge + avatar ring classes for premium members.
 * Used across PostCard, ProfilePage, PostPage, etc.
 */

const TIER_META = {
  1: {
    name: 'Quiet Mode',
    icon: Shield,
    badgeClass: 'bg-blue-500/90 text-white border-blue-400',
    ringClass: 'ring-[3px] ring-blue-400',
    smallRingClass: 'ring-2 ring-blue-400',
    gradient: 'from-blue-500 to-cyan-400',
  },
  2: {
    name: 'After Hours',
    icon: Sparkles,
    badgeClass: 'bg-gradient-to-r from-violet-600 to-indigo-800 text-white border-violet-500 animate-pulse',
    ringClass: 'ring-[3px] ring-violet-500 shadow-[0_0_12px_rgba(139,92,246,0.5)]',
    smallRingClass: 'ring-2 ring-violet-500',
    gradient: 'from-violet-600 to-indigo-900',
  },
  3: {
    name: 'Needlessly Rich',
    icon: Crown,
    badgeClass: 'bg-gradient-to-r from-rose-500 to-amber-400 text-white border-rose-400',
    ringClass: 'ring-[3px] ring-rose-400 shadow-[0_0_16px_rgba(244,63,94,0.5)]',
    smallRingClass: 'ring-2 ring-rose-400',
    gradient: 'from-rose-500 to-amber-400',
  },
};

/** Returns tier metadata for a given tier level (0 = free, 1-3 = premium) */
export const getTierMeta = (tierLevel) => TIER_META[tierLevel] || null;

/** Returns tier metadata by role name */
export const getTierMetaByName = (roleName) => {
  if (!roleName) return null;
  return Object.values(TIER_META).find(t => t.name === roleName) || null;
};

/**
 * TierBadge — renders the styled tier tag.
 * @param {number} tier - 0 (free) to 3
 * @param {string} roleName - "Quiet Mode" / "After Hours" / "Needlessly Rich"
 * @param {string} size - "sm" | "md" (default "sm")
 */
export const TierBadge = ({ tier, roleName, size = 'sm' }) => {
  const meta = tier ? getTierMeta(tier) : getTierMetaByName(roleName);
  if (!meta) return null;

  const Icon = meta.icon;
  const isSmall = size === 'sm';

  return (
    <Badge
      className={`${meta.badgeClass} ${isSmall ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5'} inline-flex items-center gap-1 font-semibold`}
      data-testid={`tier-badge-${meta.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className={isSmall ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      {meta.name}
    </Badge>
  );
};

/**
 * Returns the CSS ring class for an avatar based on premium tier.
 * @param {number} tier
 * @param {string} variant - "lg" for profile, "sm" for post cards
 */
export const getTierRingClass = (tier, variant = 'sm') => {
  const meta = getTierMeta(tier);
  if (!meta) return '';
  return variant === 'lg' ? meta.ringClass : meta.smallRingClass;
};

/**
 * Premium cover banner gradient for profile pages.
 * Returns a CSS gradient class string, or null for free users.
 */
export const getTierCoverGradient = (tier) => {
  switch (tier) {
    case 1: return 'from-blue-600 to-cyan-500 dark:from-blue-800 dark:to-cyan-700';
    case 2: return 'from-violet-700 to-indigo-900 dark:from-violet-800 dark:to-indigo-950';
    case 3: return 'from-rose-600 via-amber-500 to-rose-600 dark:from-rose-800 dark:via-amber-600 dark:to-rose-800';
    default: return null;
  }
};

export default TierBadge;
